import type { UserPermissionsResponse } from 'api';
import {
  ASSISTED_ONBOARDING_PHONE_NUMBER,
  MEMORY_TOUR_COMPLETED,
  MEMORY_TOUR_STEP,
} from 'constants/user';
import Cookies from 'js-cookie';
import { DateTime } from 'luxon';
import type {
  UserGlobalPermissions,
  UserPermissions,
  UserProperties,
  UserPropertiesRaw,
  UserProperty,
  UserPropertyRaw,
} from 'redux/modules/user.types';
import type { UserTimeFormat } from 'types/user';
import debug from './debug';

/**
 * Some properties look like valid numbers but should still be treated as strings, such as phone
 * numbers with international calling codes.
 */
const NON_NUMERIC_PROPERTIES: Array<string> = [ASSISTED_ONBOARDING_PHONE_NUMBER];

export function serializeProperty(userProperty: UserProperty): UserPropertyRaw {
  const { property, value, user_id } = userProperty;

  let serializedValue;

  if (Array.isArray(value)) {
    serializedValue = value.join(',');
  } else if (typeof value === 'boolean') {
    serializedValue = value ? 'yes' : 'no';
  } else if (!NON_NUMERIC_PROPERTIES.includes(property) && !isNaN(Number(value))) {
    serializedValue = Number(value);
  } else {
    serializedValue = value;
  }

  return {
    property,
    value: serializedValue as any,
    user_id,
  };
}

export function deserializeProperty(userPropertyRaw: UserPropertyRaw): UserProperty {
  const { property, value, user_id } = userPropertyRaw;

  const valueMap = {
    yes: true,
    no: false,
  } as const;
  let deserializedValue;
  const isStringConvertableToArray = value.includes(',');

  if (isStringConvertableToArray) {
    deserializedValue = value.split(',');
  } else if (value === 'yes' || value === 'no') {
    deserializedValue = valueMap[value];
  } else if (!NON_NUMERIC_PROPERTIES.includes(property) && !isNaN(Number(value))) {
    deserializedValue = Number(value);
  } else {
    deserializedValue = value;
  }

  return {
    property,
    value: deserializedValue,
    user_id,
  };
}

export function deserializeProperties(
  properties: UserPropertiesRaw,
  userId?: number
): UserProperties {
  const defaultProps: Array<UserPropertyRaw> = [
    {
      property: MEMORY_TOUR_STEP,
      value: '0',
      user_id: userId || 1,
    },
    {
      property: MEMORY_TOUR_COMPLETED,
      value: 'no',
      user_id: userId || 1,
    },
  ];

  const combinedProps: Array<UserPropertyRaw> = [...defaultProps, ...properties];

  // Return as object, based off defaults to be able to override with server properties
  return combinedProps.map(deserializeProperty).reduce<UserProperties>((result, item) => {
    result[item.property as keyof UserProperties] = item.value;
    return result;
  }, {});
}

/**
 * Flattens a `/users/current/permissions.json` response from this shape:
 * ```
 * [{ resource: 'x', permissions: [], global_permissions: [] }, ...]
 * ```
 * to `{ permissions, globalPermissions }` objects.
 */
export const flattenUserPermissions = (response: UserPermissionsResponse) => {
  const permissions = {} as UserPermissions;
  const globalPermissions = {} as UserGlobalPermissions;

  for (const entry of response) {
    permissions[entry.resource] = entry.permissions;
    globalPermissions[entry.resource] = entry.global_permissions ?? [];
  }

  return { permissions, globalPermissions };
};

export const USER_TIME_FORMAT_COOKIE = 'time_format';

/**
 * Store the user's bootstrapped time format in a cookie, that can then be read by public reports to
 * display the report in the user's preferred time format.
 */
export const setUserTimeFormatCookie = () => {
  try {
    // Don't attempt to set the time format if it's not set (i.e. when viewing a public report).
    if (!['12', '24'].includes(window.BOOTSTRAPPED_DATA.user.time_format?.toString())) {
      return;
    }

    Cookies.set(USER_TIME_FORMAT_COOKIE, window.BOOTSTRAPPED_DATA.user.time_format.toString(), {
      domain: `.${window.location.hostname.split('.').slice(1).join('.')}`,
      expires: 60,
      sameSite: 'Strict',
    });
  } catch (e) {
    debug.error(e);
  }
};

/**
 * Tries to infer the user's preferred time format for public reports.
 * @returns Inferred time format, or defaults to 24.
 */
export const readOrInferUserTimeFormat = (): UserTimeFormat => {
  try {
    // Try to see if the user has a time format cookie set (they should, if they use Timely).
    const timeFormatCookie = Cookies.get(USER_TIME_FORMAT_COOKIE);
    if (timeFormatCookie && ['12', '24'].includes(timeFormatCookie)) {
      return +timeFormatCookie;
    }

    // If there is no stored time format, try to (naïvely) infer it from a formatted date string.
    const timeFormatString = new Date().toLocaleString();
    if (timeFormatString.match(/am|pm/i)) {
      return 12;
    }
  } catch (e) {
    debug.error(e);
  }

  return 24;
};

/**
 * Checks if the local timezone's UTC offset differs from the user configured offset.
 * Beware that Chrome has bugs here if user overrides their timezone in system settings.
 * @link https://support.google.com/chrome/thread/94734423?hl=en&msgid=95579769
 */
export function checkTimezoneMismatch(userTz: string, detectedTz: string): boolean {
  const now = DateTime.local();
  return now.setZone(detectedTz).offset !== now.setZone(userTz).offset;
}
